import {
    AccessTime,
    Assignment,
    Backspace,
    Build,
    CloudUpload,
    Description,
    DeveloperBoard,
    DeviceHub,
    ExitToApp,
    Grain,
    Info,
    InfoOutlined,
    InfoTwoTone,
    List as ListIcon,
    Lock,
    Memory,
    Pageview,
    Person,
    Search,
    Send,
    SettingsApplications,
    SettingsInputComponent,
    SignalCellularAlt,
    ViewHeadline,
    DesignServices,
    ConnectingAirports
} from '@mui/icons-material'
import ApartmentIcon from '@mui/icons-material/Apartment'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import CastIcon from '@mui/icons-material/Cast'
import DvrIcon from '@mui/icons-material/Dvr'
import SettingsIcon from '@mui/icons-material/Settings'
import TimelapseIcon from '@mui/icons-material/Timelapse'
import {Collapse, IconButton, List, ListItemButton, ListItemIcon, ListItemText} from '@mui/material'
import {EquipmentIcon, ExpandIcon, IconByKey} from '@oc/icons'
import {fullPath, useAuthInfo, useCustomerFromRoute, useIsAppDrawerExpanded, useValidationState} from '@oc/shell-core'
import {getHoverStyle, makeOcStyles} from '@oc/styles'
import {observer} from 'mobx-react-lite'
import React from 'react'
import {matchPath, useLocation} from 'react-router'
import Routes from '../routes'
import {messages} from '../translations'

export interface DrawerItemProps {
    route: string
    rkId?: string
    nestLevel?: number
    hasWarning?: boolean
}

interface ReadyKitItemProps {
    rkId: string
    rkName: string
}

interface CollapsibleItemSelectableProps extends React.PropsWithChildren {
    icon: React.ComponentType
    nestLevel?: number
    label: string
    route?: string
    rkId?: string
    idPrefix?: string
    selectable: boolean
    defaultExpanded?: boolean
}

const useDrawerItemStyles = makeOcStyles<{nestLevel: number}>((theme, {nestLevel}) => ({
    icon: {
        minWidth: 30
    },
    listItem: {
        paddingLeft: theme.spacing(2 + nestLevel),
        color: theme.syskronPalette.colors.P700
    },
    collapsible: {
        paddingRight: 0
    },
    selected: {
        backgroundColor: theme.syskronPalette.colors.P500,
        color: theme.syskronPalette.contrastColors.P500,
        ...getHoverStyle(undefined, theme.syskronPalette.colors.P500)
    },
    itemText: {
        paddingLeft: '4px'
    },
    expandButton: {
        padding: theme.spacing(1)
    },
    syncAlertIcon: {
        color: '#fff',
        width: '17px',
        margin: '8px'
    }
}))

const routeToIcon = (route: string): React.ComponentType => {
    const {allOther: plainRoutes, withCustomerOnly: customerRoutes, withCustomerAndRk: rkRoutes} = Routes

    switch (route) {
        case fullPath(plainRoutes.dashboard, Routes):
        case fullPath(plainRoutes.dashboardV1, Routes):
            return ListIcon
        case fullPath(plainRoutes.globalRkMonitoring, Routes):
        case fullPath(plainRoutes.massDeploy, Routes):
            return DvrIcon
        case fullPath(plainRoutes.audit, Routes):
            return Search
        case fullPath(plainRoutes.globalSettings, Routes):
            return () => <IconByKey iconKey="parameter" rotate="right" />
        case fullPath(plainRoutes.logout, Routes):
            return ExitToApp
        case fullPath(customerRoutes.customerEdit, Routes):
            return Info
        case fullPath(customerRoutes.viewCustomer, Routes):
            return Info
        case fullPath(customerRoutes.equipmentManagement, Routes):
            return () => <EquipmentIcon size={28} />
        case fullPath(customerRoutes.viewEquipment, Routes):
            return () => <EquipmentIcon size={28} />
        case fullPath(customerRoutes.timestealerConfiguration, Routes):
            return TimelapseIcon
        case fullPath(customerRoutes.accountRkMonitoring, Routes):
            return DvrIcon
        case fullPath(customerRoutes.insight, Routes):
            return Pageview
        case fullPath(rkRoutes.network, Routes):
            return DeviceHub
        case fullPath(rkRoutes.hwAndServices, Routes):
            return ViewHeadline
        case fullPath(rkRoutes.deploy, Routes):
            return Send
        case fullPath(rkRoutes.interfaces, Routes):
            return Memory
        case fullPath(rkRoutes.registerRk, Routes):
            return Assignment
        case fullPath(rkRoutes.meda, Routes):
            return Grain
        case fullPath(rkRoutes.passwords, Routes):
            return Lock
        case fullPath(rkRoutes.rkConfig, Routes):
            return Description
        case fullPath(rkRoutes.rkMonitoring, Routes):
            return DvrIcon
        case fullPath(rkRoutes.rkSystemInformation, Routes):
            return InfoOutlined
        case fullPath(rkRoutes.rkLogs, Routes):
            return CloudUpload
        case fullPath(rkRoutes.restartService, Routes):
            return Build
        case fullPath(rkRoutes.dasValidation, Routes):
            return SignalCellularAlt
        case fullPath(rkRoutes.connectivityCheck, Routes):
            return SettingsInputComponent
        case fullPath(rkRoutes.rkMonitoringConfiguration, Routes):
            return SettingsIcon
        case fullPath(rkRoutes.ntpValidation, Routes):
            return AccessTime
        case fullPath(rkRoutes.rkSsm, Routes):
            return CastIcon
        case fullPath(plainRoutes.userManagement, Routes):
            return Person
        case fullPath(rkRoutes.decommissioning, Routes):
            return Backspace
        case fullPath(rkRoutes.dataValidation, Routes):
            return AssignmentTurnedInIcon
        case fullPath(plainRoutes.uxLab, Routes):
            return DeveloperBoard
        case fullPath(rkRoutes.kopVersionControl, Routes):
            return SettingsApplications
        case fullPath(rkRoutes.opcuaMapping, Routes):
            return () => <IconByKey iconKey="source-branch" />
        case fullPath(customerRoutes.localFlashFluidFiles, Routes):
            return DesignServices
        case fullPath(customerRoutes.fleetManagement, Routes):
            return ConnectingAirports

        default:
            throw new Error(`Icon for route ${route} is not defined`)
    }
}

const routeToHeader = (route: string): string => {
    const {allOther: plainRoutes, withCustomerOnly: customerRoutes, withCustomerAndRk: rkRoutes} = Routes

    switch (route) {
        case fullPath(plainRoutes.dashboard, Routes):
            return messages.getText('routes', 'accounts')
        case fullPath(plainRoutes.dashboardV1, Routes):
            return messages.getText('routes', 'dashboardV1')
        case fullPath(plainRoutes.globalRkMonitoring, Routes):
            return messages.getText('routes', 'monitoring')
        case fullPath(plainRoutes.massDeploy, Routes):
            return messages.getText('routes', 'massDeploy')
        case fullPath(plainRoutes.audit, Routes):
            return messages.getText('routes', 'auditTrail')
        case fullPath(plainRoutes.globalSettings, Routes):
            return messages.getText('routes', 'globalSettings')
        case fullPath(plainRoutes.userManagement, Routes):
            return messages.getText('routes', 'userManagement')
        case fullPath(plainRoutes.userManagementSettings, Routes):
            return messages.getText('routes', 'settings')
        case fullPath(plainRoutes.logout, Routes):
            return messages.getText('routes', 'logout')
        case fullPath(customerRoutes.viewCustomer, Routes):
            return messages.getText('routes', 'basicInformationV1')
        case fullPath(customerRoutes.customerEdit, Routes):
            return messages.getText('routes', 'basicInformation')
        case fullPath(customerRoutes.equipmentManagement, Routes):
            return messages.getText('routes', 'equipment')
        case fullPath(customerRoutes.viewEquipment, Routes):
            return messages.getText('routes', 'equipmentV2')
        case fullPath(customerRoutes.timestealerConfiguration, Routes):
            return messages.getText('routes', 'timestealerConfiguration')
        case fullPath(customerRoutes.accountRkMonitoring, Routes):
            return messages.getText('routes', 'rkMonitoring')
        case fullPath(customerRoutes.insight, Routes):
            return messages.getText('routes', 'insight')
        case fullPath(customerRoutes.fleetManagement, Routes):
            return messages.getText('routes', 'fleetManagement')
        case fullPath(rkRoutes.network, Routes):
            return messages.getText('routes', 'network')
        case fullPath(rkRoutes.hwAndServices, Routes):
            return messages.getText('routes', 'hwAndServices')
        case fullPath(rkRoutes.deploy, Routes):
            return messages.getText('routes', 'deployment')
        case fullPath(rkRoutes.interfaces, Routes):
            return messages.getText('routes', 'interfaces')
        case fullPath(rkRoutes.registerRk, Routes):
            return messages.getText('routes', 'registration')
        case fullPath(rkRoutes.meda, Routes):
            return messages.getText('routes', 'meDaConfig')
        case fullPath(rkRoutes.passwords, Routes):
            return messages.getText('routes', 'passwords')
        case fullPath(rkRoutes.rkConfig, Routes):
            return messages.getText('routes', 'configurationFiles')
        case fullPath(rkRoutes.rkMonitoring, Routes):
            return messages.getText('routes', 'rkMonitoring')
        case fullPath(rkRoutes.rkSystemInformation, Routes):
            return messages.getText('routes', 'rkSystemInformation')
        case fullPath(rkRoutes.rkLogs, Routes):
            return messages.getText('routes', 'rklogs')
        case fullPath(rkRoutes.restartService, Routes):
            return messages.getText('routes', 'restartService')
        case fullPath(rkRoutes.dasValidation, Routes):
            return messages.getText('routes', 'DASValidation')
        case fullPath(rkRoutes.connectivityCheck, Routes):
            return messages.getText('routes', 'connectivityCheck')
        case fullPath(rkRoutes.rkMonitoringConfiguration, Routes):
            return messages.getText('routes', 'monitoringConfiguration')
        case fullPath(rkRoutes.ntpValidation, Routes):
            return messages.getText('routes', 'ntpValidation')
        case fullPath(rkRoutes.rkSsm, Routes):
            return messages.getText('routes', 'rkSmm')
        case fullPath(rkRoutes.decommissioning, Routes):
            return messages.getText('routes', 'decommissioning')
        case fullPath(rkRoutes.dataValidation, Routes):
            return messages.getText('routes', 'dataValidation')
        case fullPath(plainRoutes.uxLab, Routes):
            return messages.getText('routes', 'uxLab')
        case fullPath(rkRoutes.kopVersionControl, Routes):
            return messages.getText('routes', 'versionControl')
        case fullPath(rkRoutes.opcuaMapping, Routes):
            return messages.getText('routes', 'opcUaMapping')
        case fullPath(customerRoutes.localFlashFluidFiles, Routes):
            return messages.getText('routes', 'flashFluidFiles')
        default:
            throw new Error(`Header for route ${route} is not defined`)
    }
}

const useIsSelected = (route: string, rkId: string | undefined): boolean => {
    const {pathname} = useLocation()
    const path = matchPath({path: route, end: true}, pathname)

    return !!path && (!rkId || rkId === path.params.rkId)
}

const useHref = (route: string, rkId: string | undefined) => {
    const {customerId} = useCustomerFromRoute()

    return route.replace(/:customerId/g, customerId || '').replace(/:rkId/g, rkId || '')
}

const useNavigate = (route: string, rkId: string | undefined) => {
    const path = useHref(route, rkId)
    const validationState = useValidationState()

    return React.useCallback(() => {
        validationState.pushToHistory(path)
    }, [validationState, path])
}

const useLogout = () => {
    const {signOut} = useAuthInfo()
    const validationState = useValidationState()

    return React.useCallback(async () => {
        if (validationState.isDirty()) {
            validationState.setErrorMessage()
            return
        }

        await signOut()
        localStorage.clear()
    }, [validationState, signOut])
}

const useNavigateToRoute = (route: string, rkId: string | undefined): ((e: React.SyntheticEvent) => Promise<void>) => {
    const logout = useLogout()
    const navigate = useNavigate(route, rkId)

    return React.useCallback(
        async (e: React.SyntheticEvent) => {
            e.preventDefault()
            switch (route) {
                case '/logout':
                    return await logout()
                default:
                    return navigate()
            }
        },
        [route, logout, navigate]
    )
}

function _DrawerItem(props: DrawerItemProps) {
    const {route, rkId, nestLevel = 0} = props
    const Icon = routeToIcon(route)
    const header = routeToHeader(route)
    const navigateToRoute = useNavigateToRoute(route, rkId)
    const {classes, cx} = useDrawerItemStyles({nestLevel})
    const selected = useIsSelected(route, rkId)
    const href = useHref(route, rkId)
    const menuExpanded = useIsAppDrawerExpanded()

    return (
        <ListItemButton
            component="a"
            href={route !== '/logout' ? href : undefined}
            onClick={navigateToRoute}
            className={cx({
                [classes.listItem]: true,
                [classes.selected]: selected
            })}
            title={menuExpanded ? undefined : header}
        >
            <ListItemIcon className={classes.icon} style={{color: 'inherit'}}>
                <Icon />
            </ListItemIcon>
            {menuExpanded && <ListItemText primary={header} className={classes.itemText} />}
            {props.hasWarning && (
                <InfoTwoTone
                    sx={{
                        '& path:nth-of-type(1)': {fill: '#ff7314', opacity: 1},
                        '& path:nth-of-type(2)': {fill: '#fff'}
                    }}
                    className={classes.syncAlertIcon}
                />
            )}
        </ListItemButton>
    )
}
export const DrawerItem = observer(_DrawerItem)

function _ReadyKitItem(props: ReadyKitItemProps) {
    const {rkId, rkName} = props
    const route = fullPath(Routes.withCustomerAndRk.rkMonitoring, Routes)
    const nestLevel = 1
    const {classes} = useDrawerItemStyles({nestLevel})
    const navigateToRoute = useNavigateToRoute(route, rkId)
    const href = useHref(route, rkId)
    const menuExpanded = useIsAppDrawerExpanded()

    return (
        <ListItemButton
            component="a"
            onClick={navigateToRoute}
            className={classes.listItem}
            href={href}
            title={menuExpanded ? undefined : rkName}
        >
            <ListItemIcon className={classes.icon}>
                <ApartmentIcon />
            </ListItemIcon>
            {menuExpanded && <ListItemText primary={rkName} className={classes.itemText} />}
        </ListItemButton>
    )
}
export const ReadyKitItem = observer(_ReadyKitItem)

function _CollapsibleItemSelectable(props: CollapsibleItemSelectableProps) {
    const {children, icon: Icon, nestLevel = 0, label, route, rkId, idPrefix, selectable, defaultExpanded} = props
    const {classes, cx} = useDrawerItemStyles({nestLevel})
    const [expanded, setExpanded] = React.useState(defaultExpanded || false)
    const navigateToRoute = useNavigateToRoute(route || '', rkId)
    const href = useHref(route || '', rkId)
    const selected = useIsSelected(route || '', rkId)
    const onTogleExpand = React.useCallback(
        (event: React.SyntheticEvent) => {
            event.stopPropagation()
            event.preventDefault()
            const nextExpanded = !expanded
            setExpanded(nextExpanded)
        },
        [expanded, setExpanded]
    )
    const menuExpanded = useIsAppDrawerExpanded()

    return (
        <>
            <ListItemButton
                component={selectable ? 'a' : 'div'}
                onClick={selectable ? navigateToRoute : onTogleExpand}
                className={cx(classes.listItem, classes.collapsible, {
                    [classes.selected]: selected && selectable
                })}
                href={selectable ? href : undefined}
                title={menuExpanded ? undefined : label}
            >
                <ListItemIcon className={classes.icon} style={{color: 'inherit'}}>
                    <Icon />
                </ListItemIcon>
                {menuExpanded && <ListItemText primary={label} className={classes.itemText} />}
                <IconButton
                    onClick={onTogleExpand}
                    data-cy={`${idPrefix}-expand-button`}
                    color="inherit"
                    className={classes.expandButton}
                    size="large"
                >
                    {expanded ? (
                        <ExpandIcon rotate="down" size="16px" color="inherit" />
                    ) : (
                        <ExpandIcon size="16px" color="inherit" />
                    )}
                </IconButton>
            </ListItemButton>
            <Collapse in={expanded} timeout="auto">
                <List disablePadding>{children}</List>
            </Collapse>
        </>
    )
}
export const CollapsibleItemSelectable = observer(_CollapsibleItemSelectable)
